import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  AlertTriangle,
  CheckCircle,
  Search,
  Edit,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CalendarCheck,
  CalendarX,
  X,
  Wrench,
  AlertOctagon,
  Filter
} from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';

const GestionControles = ({ onEditOutil, onRefresh }) => {
  const [outils, setOutils] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterAtelier, setFilterAtelier] = useState('all');
  const [ateliers, setAteliers] = useState([]);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedOutil, setSelectedOutil] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    depasse: true,
    proche: true,
    ok: false
  });
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);

      const { data: outilsData, error: outilsError } = await supabase
        .from('v_mat_outils_complets')
        .select('*')
        .or('necessite_controle.eq.true,necessite_remplacement.eq.true')
        .order('date_prochain_controle', { ascending: true, nullsFirst: false });

      if (outilsError) throw outilsError;

      const { data: ateliersData } = await supabase
        .from('mat_ateliers')
        .select('*')
        .order('nom');

      setOutils(outilsData || []);
      setAteliers(ateliersData || []);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const outilsParStatut = useMemo(() => {
    let filtered = outils;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(o => 
        o.nom?.toLowerCase().includes(term) ||
        o.reference?.toLowerCase().includes(term)
      );
    }

    if (filterAtelier !== 'all') {
      filtered = filtered.filter(o => o.atelier_code === filterAtelier);
    }

    if (filterType !== 'all') {
      if (filterType === 'controle') {
        filtered = filtered.filter(o => o.necessite_controle);
      } else if (filterType === 'remplacement') {
        filtered = filtered.filter(o => o.necessite_remplacement);
      }
    }

    const depasses = filtered.filter(o => 
      o.statut_alerte === 'controle_depasse' || o.statut_alerte === 'remplacement_depasse'
    );
    const proches = filtered.filter(o => 
      o.statut_alerte === 'controle_proche' || o.statut_alerte === 'remplacement_proche'
    );
    const ok = filtered.filter(o => o.statut_alerte === 'ok');

    if (filterStatut === 'depasse') return { depasses, proches: [], ok: [] };
    if (filterStatut === 'proche') return { depasses: [], proches, ok: [] };
    if (filterStatut === 'ok') return { depasses: [], proches: [], ok };

    return { depasses, proches, ok };
  }, [outils, searchTerm, filterAtelier, filterType, filterStatut]);

  const stats = useMemo(() => ({
    total: outils.length,
    depasses: outils.filter(o => 
      o.statut_alerte === 'controle_depasse' || o.statut_alerte === 'remplacement_depasse'
    ).length,
    proches: outils.filter(o => 
      o.statut_alerte === 'controle_proche' || o.statut_alerte === 'remplacement_proche'
    ).length,
    ok: outils.filter(o => o.statut_alerte === 'ok').length
  }), [outils]);

  const handleUpdateControle = async (outil, nouvelleDate) => {
    try {
      setUpdating(true);
      
      const updates = {
        date_dernier_controle: new Date().toISOString().split('T')[0],
        date_prochain_controle: nouvelleDate
      };

      const { error } = await supabase
        .from('mat_outils')
        .update(updates)
        .eq('id', outil.id);

      if (error) throw error;

      await loadData();
      if (onRefresh) onRefresh();
      setShowUpdateModal(false);
      setSelectedOutil(null);
    } catch (error) {
      console.error('Erreur mise à jour:', error);
      alert('Erreur: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getStatusBadge = (outil) => {
    switch (outil.statut_alerte) {
      case 'controle_depasse':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs flex items-center gap-1">
          <AlertOctagon className="w-3 h-3" /> Contrôle dépassé
        </span>;
      case 'remplacement_depasse':
        return <span className="px-2 py-1 bg-red-500/20 text-red-400 rounded-full text-xs flex items-center gap-1">
          <AlertOctagon className="w-3 h-3" /> Remplacement dépassé
        </span>;
      case 'controle_proche':
        return <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Contrôle proche
        </span>;
      case 'remplacement_proche':
        return <span className="px-2 py-1 bg-orange-500/20 text-orange-400 rounded-full text-xs flex items-center gap-1">
          <AlertTriangle className="w-3 h-3" /> Remplacement proche
        </span>;
      default:
        return <span className="px-2 py-1 bg-green-500/20 text-green-400 rounded-full text-xs flex items-center gap-1">
          <CheckCircle className="w-3 h-3" /> OK
        </span>;
    }
  };

  const getStatusColor = (outil) => {
    switch (outil.statut_alerte) {
      case 'controle_depasse':
      case 'remplacement_depasse':
        return 'border-red-500 bg-red-500/10';
      case 'controle_proche':
      case 'remplacement_proche':
        return 'border-orange-500 bg-orange-500/10';
      default:
        return 'border-green-500 bg-green-500/10';
    }
  };

  const OutilCard = ({ outil }) => (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`p-4 rounded-xl border-2 ${getStatusColor(outil)} transition-all hover:scale-[1.02]`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          {outil.photo_url ? (
            <img src={outil.photo_url} alt={outil.nom} className="w-16 h-16 rounded-lg object-cover" />
          ) : (
            <div className="w-16 h-16 rounded-lg bg-slate-700 flex items-center justify-center">
              <Wrench className="w-8 h-8 text-gray-400" />
            </div>
          )}
          <div className="flex-1">
            <h4 className="text-white font-semibold">{outil.nom}</h4>
            <p className="text-sm text-gray-400">{outil.reference}</p>
            <p className="text-xs text-gray-500 mt-1">
              {outil.emplacement_nom} • {outil.atelier_nom}
            </p>
          </div>
        </div>
        
        <div className="flex flex-col items-end gap-2">
          {getStatusBadge(outil)}
          
          <div className="text-right text-sm">
            {outil.necessite_controle && outil.date_prochain_controle && (
              <div className="flex items-center gap-2 text-gray-400">
                <CalendarCheck className="w-4 h-4" />
                <span>
                  Contrôle: {new Date(outil.date_prochain_controle).toLocaleDateString('fr-FR')}
                  {outil.jours_avant_controle !== null && (
                    <span className={`ml-2 ${outil.jours_avant_controle < 0 ? 'text-red-400' : outil.jours_avant_controle < 30 ? 'text-orange-400' : 'text-green-400'}`}>
                      ({outil.jours_avant_controle < 0 ? `${Math.abs(outil.jours_avant_controle)}j de retard` : `dans ${outil.jours_avant_controle}j`})
                    </span>
                  )}
                </span>
              </div>
            )}
            {outil.necessite_remplacement && outil.date_remplacement_prevue && (
              <div className="flex items-center gap-2 text-gray-400 mt-1">
                <CalendarX className="w-4 h-4" />
                <span>
                  Remplacement: {new Date(outil.date_remplacement_prevue).toLocaleDateString('fr-FR')}
                  {outil.jours_avant_remplacement !== null && (
                    <span className={`ml-2 ${outil.jours_avant_remplacement < 0 ? 'text-red-400' : outil.jours_avant_remplacement < 30 ? 'text-orange-400' : 'text-green-400'}`}>
                      ({outil.jours_avant_remplacement < 0 ? `${Math.abs(outil.jours_avant_remplacement)}j de retard` : `dans ${outil.jours_avant_remplacement}j`})
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex gap-2 mt-4 pt-4 border-t border-slate-700">
        {outil.necessite_controle && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setSelectedOutil(outil);
              setShowUpdateModal(true);
            }}
            className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm flex items-center justify-center gap-2"
          >
            <CheckCircle className="w-4 h-4" />
            Marquer contrôlé
          </motion.button>
        )}
        {onEditOutil && (
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onEditOutil(outil)}
            className="px-3 py-2 bg-slate-600 hover:bg-slate-500 text-white rounded-lg text-sm flex items-center justify-center gap-2"
          >
            <Edit className="w-4 h-4" />
            Modifier
          </motion.button>
        )}
      </div>
    </motion.div>
  );

  const Section = ({ title, icon: Icon, colorClass, bgClass, outils: sectionOutils, sectionKey }) => (
    <div className="mb-6">
      <button
        onClick={() => toggleSection(sectionKey)}
        className={`w-full flex items-center justify-between p-4 rounded-xl bg-slate-800/50 border ${bgClass} hover:bg-slate-700/50 transition-colors`}
      >
        <div className="flex items-center gap-3">
          <Icon className={`w-6 h-6 ${colorClass}`} />
          <h3 className="text-xl font-semibold text-white">{title}</h3>
          <span className={`px-3 py-1 rounded-full text-sm ${bgClass} ${colorClass}`}>
            {sectionOutils.length}
          </span>
        </div>
        {expandedSections[sectionKey] ? (
          <ChevronUp className="w-5 h-5 text-gray-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400" />
        )}
      </button>
      
      <AnimatePresence>
        {expandedSections[sectionKey] && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              {sectionOutils.length > 0 ? (
                sectionOutils.map(outil => <OutilCard key={outil.id} outil={outil} />)
              ) : (
                <div className="col-span-2 text-center py-8 text-gray-400">
                  Aucun outil dans cette catégorie
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
          <div className="flex items-center justify-between">
            <Calendar className="w-8 h-8 text-blue-500" />
            <span className="text-3xl font-bold text-white">{stats.total}</span>
          </div>
          <p className="text-gray-400 mt-2">Outils suivis</p>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4 border border-red-500/30">
          <div className="flex items-center justify-between">
            <AlertOctagon className="w-8 h-8 text-red-500" />
            <span className="text-3xl font-bold text-red-400">{stats.depasses}</span>
          </div>
          <p className="text-gray-400 mt-2">Dépassés</p>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4 border border-orange-500/30">
          <div className="flex items-center justify-between">
            <AlertTriangle className="w-8 h-8 text-orange-500" />
            <span className="text-3xl font-bold text-orange-400">{stats.proches}</span>
          </div>
          <p className="text-gray-400 mt-2">À venir (30j)</p>
        </div>
        
        <div className="bg-slate-800/50 rounded-xl p-4 border border-green-500/30">
          <div className="flex items-center justify-between">
            <CheckCircle className="w-8 h-8 text-green-500" />
            <span className="text-3xl font-bold text-green-400">{stats.ok}</span>
          </div>
          <p className="text-gray-400 mt-2">À jour</p>
        </div>
      </div>

      {/* Filtres */}
      <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-gray-400" />
          <span className="text-white font-medium">Filtres</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <select
            value={filterAtelier}
            onChange={(e) => setFilterAtelier(e.target.value)}
            className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous les ateliers</option>
            {ateliers.map(a => (
              <option key={a.id} value={a.code}>{a.nom}</option>
            ))}
          </select>
          
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous les types</option>
            <option value="controle">Contrôles périodiques</option>
            <option value="remplacement">Remplacements</option>
          </select>
          
          <select
            value={filterStatut}
            onChange={(e) => setFilterStatut(e.target.value)}
            className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Tous les statuts</option>
            <option value="depasse">Dépassés uniquement</option>
            <option value="proche">À venir (30j)</option>
            <option value="ok">À jour</option>
          </select>
        </div>
      </div>

      {/* Sections */}
      <Section
        title="Dépassés - Action requise"
        icon={AlertOctagon}
        colorClass="text-red-500"
        bgClass="border-red-500/30 bg-red-500/10"
        outils={outilsParStatut.depasses}
        sectionKey="depasse"
      />
      
      <Section
        title="À venir dans les 30 jours"
        icon={AlertTriangle}
        colorClass="text-orange-500"
        bgClass="border-orange-500/30 bg-orange-500/10"
        outils={outilsParStatut.proches}
        sectionKey="proche"
      />
      
      <Section
        title="À jour"
        icon={CheckCircle}
        colorClass="text-green-500"
        bgClass="border-green-500/30 bg-green-500/10"
        outils={outilsParStatut.ok}
        sectionKey="ok"
      />

      {/* Modal de mise à jour */}
      <AnimatePresence>
        {showUpdateModal && selectedOutil && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
            onClick={() => setShowUpdateModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-slate-800 rounded-2xl p-6 max-w-md w-full border border-slate-700"
              onClick={e => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-semibold text-white">Marquer comme contrôlé</h3>
                <button
                  onClick={() => setShowUpdateModal(false)}
                  className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="mb-6 p-4 bg-slate-700/50 rounded-lg">
                <p className="text-white font-medium">{selectedOutil.nom}</p>
                <p className="text-gray-400 text-sm">{selectedOutil.reference}</p>
                {selectedOutil.periodicite_controle_mois && (
                  <p className="text-blue-400 text-sm mt-2">
                    Périodicité: tous les {selectedOutil.periodicite_controle_mois} mois
                  </p>
                )}
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                handleUpdateControle(selectedOutil, formData.get('prochainControle'));
              }}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Prochain contrôle prévu
                  </label>
                  <input
                    type="date"
                    name="prochainControle"
                    defaultValue={
                      selectedOutil.periodicite_controle_mois
                        ? new Date(Date.now() + selectedOutil.periodicite_controle_mois * 30 * 24 * 60 * 60 * 1000)
                            .toISOString().split('T')[0]
                        : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                    }
                    className="w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowUpdateModal(false)}
                    className="flex-1 px-4 py-3 bg-slate-600 hover:bg-slate-500 text-white rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={updating}
                    className="flex-1 px-4 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {updating ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <CheckCircle className="w-4 h-4" />
                    )}
                    Confirmer
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default GestionControles;